<template>
<easy-data-table
	class="data-table m-0 p-0"
	table-class-name="customize-table"
	:headers="tableHeaders"
	:items="tableItems"
	theme-color="rgb(198, 199, 200)"
	sort-by="time"
	sort-type="desc"
	:filter-options="filterOptions"
	:rows-per-page="rowsPerPage"
	:table-min-height="680"
	:table-height="height"
	:loading="isLoading"
	@update-page-items="(items) => itemsInTable = items"
	no-hover
	alternating
	hide-footer
>
	<!-- If events list is empty -->
	<template #empty-message>
		Немає подій що відповідають заданим критеріям
	</template>

	<template #loading>
		<div class="spinner-border" style="width: 3rem; height: 3rem;" role="status">
			<span class="visually-hidden">Loading...</span>
		</div>
	</template>

	<!-- Filter to eventName column -->
	<template #[`header-eventName`]="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="eventNameCriterias.length > 0"
			:criterias="eventNameCriterias"
			:default-checked="eventNameCriterias"
			@checked-criterias="(criterias) => { saveFilters('eventName', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Filter to station column -->
	<template #[`header-${Station.passportKeys.name}`]="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="stationCriterias.length > 0"
			:criterias="stationCriterias"
			:default-checked="stationCriterias"
			show-search
			@checked-criterias="(criterias) => { saveFilters('station', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Filter to organization column -->
	<template #[`header-${Station.passportKeys.orgName}`]="header">
		<!-- Don't create if criterias is empty because form will empty -->
		<table-filter-checkboxes
			v-if="organizationCriterias.length > 0"
			:criterias="organizationCriterias"
			:default-checked="organizationCriterias"
			show-search
			@checked-criterias="(criterias) => { saveFilters('organization', criterias) }"
		/>
		{{ header.text }}
	</template>

	<!-- Time item customization -->
	<template #item-time="{ time }">
		{{ dateTimeToString(time, false, false, true) }}
	</template>

	<!-- Event item customization -->
	<template #item-event="{ component, event }">
		<component :is="component" v-bind="event" />
	</template>
</easy-data-table>
</template>




<script>
import { watchEffect, ref } from 'vue'
import TableFilterCheckboxes from '@/components/TableFilterCheckboxes.vue'
import StationPassportChanges from '@/components/EventsSection/StationPassportChanges.vue'
import StationStabilizationChangesRemoteEvent from '@/components/EventsSection/StationStabilizationChangesRemoteEvent.vue'
import StationStabilizationChangesLockalEvent from '@/components/EventsSection/StationStabilizationChangesLockalEvent.vue'
import CreateReportEvent from './CreateReportEvent.vue'
import StationLightningEvent from './StationLightningEvent.vue'
import StationAlertEvent from './StationAlertEvent.vue'
import StationPowerInputOutOfRangeEvent from './StationPowerInputOutOfRangeEvent.vue'
import StationUnitConnectionEvent from './StationUnitConnectionEvent.vue'
import StationFailuresEvent from './StationFailuresEvent.vue'
import StationBoxDoorStateEvent from './StationBoxDoorStateEvent.vue'
import { useAccontStore } from '@/stores/AccountStore.js'
import { useStationsStore } from '@/stores/StationsStore.js'
import { useSessionStore } from '@/stores/SessionStore.js'
import { useStationsPassportHistory } from '@/composables/stationsPassportHistory.js'
import {
	alertSchema,
	useAlerts,
} from '@/composables/alerts.js'
import {
	useEvents,
	eventSchema,
	eventTitles,
} from '@/composables/events.js'
import { useRpcTasks, rpcTaskSchema, } from '@/composables/rpcTasks.js'
import { metricSchema, useMetricHistory } from '@/composables/metricHistory.js'
import { useOrganizations, organizationSchema } from '@/composables/organizations.js'
import { Station } from '@/models/station.js'
import {
	FIELD_NAMES,
} from '@/api/api.js'
import {
	dateTimeToString,
	easyDataTableHeight,
	capitalizeFirstLetter,
} from '@/helpers.js'
import { PASSPORT_HISTORY_WRITE_BEGIN_DATE } from '@/app.conf.js'




// Events from event DB
const clearEventKeys = Object.values(eventSchema.eventType.allowed).map((el) => el.key)
// Every event visualization component
const eventComponents = {
	'station_passport_edit': StationPassportChanges,
	'create_report': CreateReportEvent,
	'alert': StationAlertEvent,
	'failures': StationFailuresEvent,
	[eventSchema.eventType.allowed.stabilizationSettings.key]: StationStabilizationChangesRemoteEvent,
	[eventSchema.eventType.allowed.stabilizationParametersChange.key]: StationStabilizationChangesLockalEvent,
	[eventSchema.eventType.allowed.lightning.key]: StationLightningEvent,
	[eventSchema.eventType.allowed.stationPowerInputOutOfRange.key]: StationPowerInputOutOfRangeEvent,
	[eventSchema.eventType.allowed.unitConnection.key]: StationUnitConnectionEvent,
	[eventSchema.eventType.allowed.stationBoxDoorState.key]: StationBoxDoorStateEvent,
}



export default {
	components: {
		TableFilterCheckboxes,
	},

	props: {
		timeFrom: {
			type: Date,
			require: false,
			default: null,
		},
		
		timeTo: {
			type: Date,
			require: false,
			default: null,
		},

		stationIds: {
			type: Array,
			require: false,
			default: new Array(),
		},

		// Which events to show
		eventsFilter: {
			type: Array,
			require: false,
			default: () => [
				'station_passport_edit',
				'create_report',
				'alert',
				'failures',
				...clearEventKeys,
			],
		},

		height: {
			type: Number,
			require: false,
			default: easyDataTableHeight(false),
		},

		rowsPerPage: {
			type: Number,
			require: false,
			default: undefined,
		},
	},

	emits: {
		'isLoading': value => typeof value === 'boolean',
		'tableStats': value => typeof value === 'object',
	},

	setup(props, { emit }) {
		const accountStore = useAccontStore()
		const stationsStore = useStationsStore()
		const sessionStore = useSessionStore()
		const {
			stationsPassportHistory,
			isLoading: isLoadingPassportHistory,
			error,
			setStationIds,
			setPeriod: setPassportHistoryPeriod,
		} = useStationsPassportHistory(
			props.stationIds,
			props.timeFrom,
			props.timeTo,
		)
		
		const {
			allEvents,
			isLoading: isLoadingEvents,
			setFilters: setEventFilters,
		} = useEvents()

		const {
			allRpcs,
			isLoading: isLoadingRpcs,
			setFilters: setRpcFilters,
		} = useRpcTasks()

		const {
			allAlerts,
			isLoading: isLoadingAlerts,
			setFilters: setAlertFilters,
		} = useAlerts()

		const {
			history: metricsHistory,
			isLoading: isLoadingMetrics,
			setFilters: setMetricFilters,

		} = useMetricHistory()

		const { organizationsMap, } = useOrganizations()

		// Hiding station passport fields
		const ignoreStationPassportFields = [
			...Object.values(FIELD_NAMES),
			`${Station.passportKeys.modemUnit.self}.related_rpc`,
			`${Station.passportKeys.powerUnit.self}.related_rpc`,
			`${Station.passportKeys.serviceUnit.self}.related_rpc`,
			`${Station.passportKeys.electricMeter.self}.related_rpc`,
			`${Station.passportKeys.breaker.self}.related_rpc`,
			`${Station.passportKeys.stabilization.self}.${Station.passportKeys.stabilization.updateTime}`,
			`${Station.passportKeys.stabilization.self}.${Station.passportKeys.stabilization.actor}`,
			Station.passportKeys.surveyStartTime, 
			`${Station.passportKeys.location}.type`,
			Station.passportKeys.creatorId,
			Station.passportKeys.updateBy,
			Station.passportKeys.orgId,
		]
		// Show station passport fields (Applies only to station creation event)
		const allowStationPassportFields = [
			Station.passportKeys.name,
			Station.passportKeys.shortName,
			Station.passportKeys.org_id,
			Station.passportKeys.ip,
			Station.passportKeys.modemUnit.self,
			`${Station.passportKeys.modemUnit.self}.${Station.passportKeys.modemUnit.unitVersion}`,
			`${Station.passportKeys.modemUnit.self}.${Station.passportKeys.modemUnit.unitPort}`,
			`${Station.passportKeys.modemUnit.self}.${Station.passportKeys.modemUnit.unitModbusAddr}`,
			Station.passportKeys.powerUnit.self,
			`${Station.passportKeys.powerUnit.self}.${Station.passportKeys.powerUnit.unitVersion}`,
			`${Station.passportKeys.powerUnit.self}.${Station.passportKeys.powerUnit.unitPort}`,
			`${Station.passportKeys.powerUnit.self}.${Station.passportKeys.powerUnit.unitModbusAddr}`,
			Station.passportKeys.serviceUnit.self,
			`${Station.passportKeys.serviceUnit.self}.${Station.passportKeys.serviceUnit.unitVersion}`,
			`${Station.passportKeys.serviceUnit.self}.${Station.passportKeys.serviceUnit.unitPort}`,
			`${Station.passportKeys.serviceUnit.self}.${Station.passportKeys.serviceUnit.unitModbusAddr}`,
		]
		// Table
		// Collect isLoading for table from all loadings
		let isLoading = ref(false)
		watchEffect(() => {
			isLoading.value = isLoadingPassportHistory.value
				|| isLoadingEvents.value
				|| isLoadingRpcs.value
				|| isLoadingAlerts.value
				|| isLoadingMetrics.value
			
			emit('isLoading', isLoading.value)
		})


		watchEffect(() => {
			// console.log('change props', props.timeFrom.toISOString(), props.timeTo.toISOString(), props.stationIds.length)
			setPassportHistoryPeriod(props.timeFrom, props.timeTo)

			setEventFilters({
				fromTime: props.timeFrom,
				toTime: props.timeTo,
				eventTypes: props.eventsFilter,
				stationIds: props.stationIds,
			})

			setRpcFilters({
				fromTime: props.timeFrom,
				toTime: props.timeTo,
				// senderId: null,
				destinationStationIds: accountStore.isPrimary ? null : props.stationIds,
				destinationOrganizationId: accountStore.isPrimary ? null : accountStore.organization[FIELD_NAMES.id],
				state: rpcTaskSchema.state.allowed.completeSuccess.key,
				isComplete: true,
				jobs: [
					rpcTaskSchema.job.allowed.buildStationReport.key,
					rpcTaskSchema.job.allowed.buildOrganizationReport.key,
				],
			})

			setAlertFilters({
				fromTime: props.timeFrom,
				toTime: props.timeTo,
				stationIds: props.stationIds,
			})

			setMetricFilters({
				fromTime: props.timeFrom,
				toTime: props.timeTo,
				metricsId: Station.failureMetrics,
				isOnlyUniqueMetrics: true,
			})
		})

		return {
			Station,
			accountStore,
			stationsStore,
			sessionStore,
			dateTimeToString,
			easyDataTableHeight,
			
			stationsPassportHistory,
			isLoadingPassportHistory,
			error,
			setStationIds,
			setPassportHistoryPeriod,

			allEvents,
			isLoadingEvents,
			eventTitles,

			allRpcs,
			isLoadingRpcs,

			allAlerts,
			isLoadingAlerts,

			metricsHistory,

			isLoading,

			organizationsMap,

			ignoreStationPassportFields,
			allowStationPassportFields,
		}
	},

	data() {
		return {
			// Table Filters
			filters: {
				eventName: [],
				organization: [],
				station: [],
			},

			itemsInTable: [],
		}
	},

	watch: {
		stationIds() {
			this.setStationIds(this.stationIds)
		},

		itemsInTable(newItems) {
			const timeFieldSorted = newItems.map((el) => el.time).sort((a, b) => a.getTime() - b.getTime())

			this.$emit('tableStats', {
				'rowsCount': this.tableItems.length,
				'displayedRowsCount': newItems.length,
				'oldestTime': new Date(timeFieldSorted[0]),
				'newestTime': new Date(timeFieldSorted.reverse()[0]),
			})
		},
	},

	created() {},

	mounted() {},

	methods: {
		saveFilters(filterName, criterias) {
			this.filters[filterName] = criterias
		},
	},

	computed: {
		tableHeaders() {
			const headers = []

			headers.push({ text: "Тип події", value: "eventName"})
			
			// If table for one station
			if (this.stationIds.length === 1) {
				// ...
			// If table for many stations
			} else {
				headers.push({ text: "Об'єкт", value: Station.passportKeys.name, sortable: true, })
				if (this.accountStore.isPrimary) {
					headers.push({ text: "Організація", value: Station.passportKeys.orgName, sortable: true, })
				}
			}

			headers.push({ text: "Час", value: "time", width: 160, sortable: true, })
			headers.push({ text: "Деталізація події", value: "event"})

			return headers
		},

		tableItems() {
			const items = []

			// Add all events for one event type
			// Use only for events from event DB
			const addEventItems = (eventKey) => {
				const filteredEvents = this.allEvents
					.filter((el) => el[eventSchema.eventType.key] == eventKey)
				const eventTitle = Object.values(eventSchema.eventType.allowed)
					.find((val) => val.key == eventKey).title
				
				for (const event of filteredEvents) {
					const eventStation = this.stationsStore.station(event[eventSchema.stationId.key])

					items.push({
						component: eventComponents[eventKey],
						eventName: capitalizeFirstLetter(eventTitle),
						[Station.passportKeys.name]: eventStation?.shortName ?? eventStation?.name,
						[Station.passportKeys.orgName]: eventStation?.passport?.get(Station.passportKeys.orgName),
						time: event[FIELD_NAMES.createTime],
						event: event[eventSchema.payload.key],
					})
				}
			}

			// Add events from event DB
			//
			const intersection = [...new Set(this.eventsFilter).intersection(new Set(clearEventKeys))]
			intersection.forEach((eventKey) => addEventItems(eventKey))

			// Add other events
			//
			// Passport (settings) edit event
			if (this.eventsFilter.includes('station_passport_edit')) {
				// Iterate stations
				for (const [stationId, passport] of Object.entries(this.stationsPassportHistory)) {
					const actualStation = this.stationsStore.station(stationId)
					
					// If create station event
					if (passport[0]?._version == 1
						&& passport[0][FIELD_NAMES.updateTime].getTime() > PASSPORT_HISTORY_WRITE_BEGIN_DATE.getTime()
					) {
						items.push({
							component: StationPassportChanges,
							eventName: eventTitles.createStationWebIf,
							[Station.passportKeys.name]: actualStation?.shortName ?? actualStation?.name,
							[Station.passportKeys.orgName]: actualStation?.passport.get(Station.passportKeys.orgName),
							time: passport[0][FIELD_NAMES.updateTime],
							event: {
								state: passport[0],
								prevState: null,
								allowFields: this.allowStationPassportFields,
							},
						})
					}

					// Iterate through passport entries in pairs, from old to new
					for (let i = 0; i < passport.length - 1; i++) {
						const prevState = passport[i]
						const state = passport[i + 1]

						items.push({
							component: StationPassportChanges,
							eventName: eventTitles.updateStationWebIf,
							[Station.passportKeys.name]: actualStation?.shortName ?? actualStation?.name,
							[Station.passportKeys.orgName]: actualStation?.passport?.get(Station.passportKeys.orgName),
							time: state[FIELD_NAMES.updateTime],
							event: {
								state: state,
								prevState: prevState,
								ignoreFields: this.ignoreStationPassportFields,
							}
						})
					}
				}
			}

			// Create station or organization report events
			if (this.eventsFilter.includes('create_report')) {
				for (const rpc of this.allRpcs) {
					const rpcStation = this.stationsStore.station(rpc[rpcTaskSchema.destinationStationId.key])
					const organization = this.organizationsMap.get(
						rpcStation
							? rpcStation.passport.get(Station.passportKeys.orgId)
							: rpc[rpcTaskSchema.destinationOrganizationId.key]
					)
			
					items.push({
						component: CreateReportEvent,
						eventName: eventTitles.createReport,
						[Station.passportKeys.name]: rpcStation ? (rpcStation.shortName ?? rpcStation.name) : '-',
						[Station.passportKeys.orgName]: organization ? organization[organizationSchema.title.key] : '-',
						time: rpc[rpcTaskSchema.createTime.key],
						event: rpc,
					})
				}
			}

			// Station alert events
			if (this.eventsFilter.includes('alert')) {
				for (const alert of this.allAlerts) {
					const alertStation = this.stationsStore.station(alert[alertSchema.stationId.key])

					items.push({
						component: StationAlertEvent,
						eventName: capitalizeFirstLetter(eventTitles.alert),
						[Station.passportKeys.name]: alertStation?.shortName ?? alertStation?.name,
						[Station.passportKeys.orgName]: alertStation?.passport?.get(Station.passportKeys.orgName),
						time: alert[alertSchema.time.key],
						event: alert,
					})
				}
			}

			// Station unit flag and metric events
			if (this.eventsFilter.includes('failures')) {
				for (const metric of this.metricsHistory) {
					const metricStation = this.stationsStore.station(metric[metricSchema.stationId.key])

					items.push({
						component: StationFailuresEvent,
						eventName: capitalizeFirstLetter(eventTitles.failure),
						[Station.passportKeys.name]: metricStation?.shortName ?? metricStation?.name,
						[Station.passportKeys.orgName]: metricStation?.passport?.get(Station.passportKeys.orgName),
						time: metric[metricSchema.time.key],
						event: metric,
					})
				}
			}
			
			return items
		},

		eventNameCriterias() {
			return [...new Set(
				this.tableItems.map((item) => item['eventName'])
			)].toSorted((a, b) => a.localeCompare(b, 'uk'))
		},

		stationCriterias() {
			return [...new Set(
				this.tableItems.map((item) => item[Station.passportKeys.name])
			)].toSorted((a, b) => a.localeCompare(b, 'uk'))
		},

		organizationCriterias() {
			return [...new Set(
				this.tableItems.map((item) => item[Station.passportKeys.orgName])
			)].toSorted((a, b) => a.localeCompare(b, 'uk'))
		},

		filterOptions() {
			const filterOptionsArray = new Array()

			filterOptionsArray.push({
				field: 'eventName',
				comparison: 'in',
				criteria: this.filters['eventName'],
			})

			if (this.stationIds.length > 1) {
				filterOptionsArray.push({
					field: Station.passportKeys.name,
					comparison: 'in',
					criteria: this.filters.station,
				})

				if (this.accountStore.isPrimary) {
					filterOptionsArray.push({
						field: Station.passportKeys.orgName,
						comparison: 'in',
						criteria: this.filters.organization,
					})
				}
			}

			return filterOptionsArray;
		},
	},
}
</script>


<style src="@/assets/table.css" scoped></style>
<style scoped>
</style>